import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "../../utils/cn";

export interface TextFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  label?: ReactNode;
  /** Helper text below the field; replaced by `error` when present. */
  hint?: ReactNode;
  /** Error message; also sets aria-invalid and the error border. */
  error?: ReactNode;
}

/* A labelled single-line input. The visual style matches the editor forms
 * (AgentEditor/ConfigEditor); supports any native input type, e.g. "date". */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField({ label, hint, error, id, className, ...rest }, ref) {
    const autoId = useId();
    const inputId = id ?? autoId;
    const messageId = `${inputId}-message`;
    const message = error ?? hint;

    return (
      <div className={cn("flex flex-col gap-1.5", className)}>
        {label && (
          <label
            htmlFor={inputId}
            className="text-body-sm font-medium text-fg-1"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={message ? messageId : undefined}
          className={cn(
            "h-9 w-full rounded-md border bg-surface px-2.5 text-body-sm text-fg-1",
            "outline-none transition-[border-color,box-shadow] duration-fast",
            "placeholder:text-fg-3 disabled:cursor-not-allowed disabled:opacity-50",
            error
              ? "border-error-border focus:border-error-border"
              : "focus:border-focus focus:shadow-focus",
          )}
          {...rest}
        />
        {message && (
          <p
            id={messageId}
            className={cn(
              "text-body-sm",
              error ? "text-error-fg" : "text-fg-2",
            )}
          >
            {message}
          </p>
        )}
      </div>
    );
  },
);
